(function () {
  'use strict';

  // Utility helpers
  const escapeHTML = (str) => {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const toCents = (val) => {
    if (val == null) return null;
    const num = parseFloat(val);
    if (Number.isNaN(num)) return null;
    return Math.round(num * 100);
  };

  const formatMoney = (cents, currencyCode) => {
    try {
      if (typeof window !== 'undefined' && window.theme) {
        if (theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
          return `<span class="theme-money">${theme.Shopify.formatMoney(cents, theme.money_format_with_code_preference)}</span>`;
        }
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return `<span class="theme-money">${window.Shopify.formatMoney(cents, theme.money_format_with_code_preference)}</span>`;
        }
      }
    } catch (e) {}
    try {
      const amount = (cents || 0) / 100;
      const nf = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      });
      return `<span class="theme-money">${nf.format(amount)}</span>`;
    } catch (e) {
      const amount = ((cents || 0) / 100).toFixed(2);
      const sym = currencyCode === 'USD' ? '$' : '';
      return `<span class="theme-money">${sym}${amount}</span>`;
    }
  };

  const pricesFromProduct = (p) => {
    const priceCents = toCents(p?.priceInfo?.price);
    const originalCents = toCents(p?.priceInfo?.originalPrice);
    const onSale =
      typeof priceCents === 'number' &&
      typeof originalCents === 'number' &&
      originalCents > priceCents;
    return { priceCents, originalCents, onSale };
  };

  const isSoldOut = (p) => {
    if (typeof p?.totalInventory === 'number') {
      if (p.totalInventory <= 0) return true;
    }
    if (Array.isArray(p?.variants) && p.variants.length) {
      const anyForSale = p.variants.some((v) => !!v.availableForSale);
      return !anyForSale;
    }
    return false;
  };

  const equalAspectRatio = (a, b) => {
    if (!a || !b) return false;
    if (!a.width || !a.height || !b.width || !b.height) return false;
    const r1 = a.width / a.height;
    const r2 = b.width / b.height;
    return Math.abs(r1 - r2) < 0.01;
  };

  const responsiveImage = ({ url, width, height, alt }) => {
    const safeAlt = escapeHTML(alt || '');
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const paddingTop = w > 0 && h > 0 ? (h / w) * 100 : 100;
    const dimAttrs = w > 0 && h > 0 ? ` width="${w}" height="${h}"` : '';
    return `
      <div class="rimage-outer-wrapper">
        <div class="rimage-wrapper" style="padding-top:${paddingTop}%;">
          <img class="rimage__image" src="${url}" alt="${safeAlt}" loading="lazy" decoding="async"${dimAttrs} />
        </div>
      </div>
    `;
  };

  const productLabelMarkup = ({ onSale, soldOut }) => {
    if (soldOut) {
      return '<span class="product-label unavailable"><span>Sold out</span></span>';
    }
    if (onSale) {
      return '<span class="product-label sale"><span>Sale</span></span>';
    }
    return '';
  };

  const renderRating = (rating, href) => {
    if (!rating) return '';
    const avg = Number(rating.averageRating);
    const count = Number(rating.ratingCount) || 0;
    if (!Number.isFinite(avg) || count <= 0) return '';
    const rounded = Math.round(avg * 2) / 2; // nearest 0.5
    const decimal = rounded % 1 !== 0 ? 0.5 : 0;
    const activeCount = Math.ceil(rounded);
    const starSvg = () => (
      '<svg class="cc-rating-stars__svg" style="display:inline-block" width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.43579 4.9459L13.63 4.9536L9.42653 8.01929L11.0329 12.97L6.81981 9.91778L2.60673 12.97L4.20346 8.01929L0 4.9536L5.20383 4.9459L6.81981 0L8.43579 4.9459Z"/>' +
      '</svg>'
    );

    const inactive = Array.from({ length: 5 }).map(starSvg).join('');
    const active = Array.from({ length: activeCount }).map(starSvg).join('');

    const aria = `${rounded} / 5`;
    const countHtml = count > 0 ? `<span class="cc-rating-custom-caption">(${count})</span>` : '';

    return `
      <a href="${href}" class="themed-product-reviews" tabindex="-1">
        <div class="cc-rating" role="img" data-rating-decimal="${decimal}" aria-label="${escapeHTML(aria)}">
          <span class="cc-rating-stars">
            <span class="cc-rating-stars--inactive">${inactive}</span>
            <span class="cc-rating-stars--active">${active}</span>
          </span>
        </div>
        ${countHtml}
      </a>
    `;
  };

  // Custom Element
  class ProductCustomProduct extends HTMLElement {
    static get observedAttributes() {
      return ['show-vendor', 'product-class', 'loop-index'];
    }

    constructor() {
      super();
      this._product = null;
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    attributeChangedCallback() {
      if (this._product) this.render();
    }

    connectedCallback() {
      if (this._product) this.render();
    }

    render() {
      const p = this._product;
      if (!p) {
        this.innerHTML = '';
        return;
      }

      const showVendorAttr = this.getAttribute('show-vendor');
      const showVendor = !(showVendorAttr === null || showVendorAttr === 'false');
      const extraClass = this.getAttribute('product-class') || '';
      const loopIndex = this.getAttribute('loop-index') || (p.index != null ? String(p.index) : '');

      const url = p.url || '#';
      const title = p.title || '';
      const brand = p.brand || '';
      const currencyCode = p?.priceInfo?.currencyCode || 'USD';

      const primary = p?.img
        ? { url: p.img.uri, width: p.img.width, height: p.img.height }
        : (Array.isArray(p?.images) && p.images[0]
            ? { url: p.images[0].url, width: p.images[0].width, height: p.images[0].height }
            : null);

      const secondary = p?.imgHover
        ? { url: p.imgHover.uri, width: p.imgHover.width, height: p.imgHover.height }
        : (Array.isArray(p?.images) && p.images[1]
            ? { url: p.images[1].url, width: p.images[1].width, height: p.images[1].height }
            : null);

      const { priceCents, originalCents, onSale } = pricesFromProduct(p);
      const soldOut = isSoldOut(p);
      const sameAspect = equalAspectRatio(primary, secondary);

      const safeTitle = escapeHTML(title);
      const safeBrand = escapeHTML(brand);

      const labelHtml = productLabelMarkup({ onSale, soldOut });

      const priceHtml = (() => {
        const curr = typeof priceCents === 'number' ? formatMoney(priceCents, currencyCode) : '';
        const was = onSale && typeof originalCents === 'number' ? `<span class="was-price theme-money">${formatMoney(originalCents, currencyCode).replace('<span class="theme-money">','').replace('</span>','')}</span>` : '';
        return `<div class="price">${curr}${was ? ' ' + was : ''}</div>`;
      })();

      const vendorHtml = showVendor && safeBrand ? `<span class="vendor">${safeBrand}</span>` : '';

      const ratingHtml = renderRating(p.rating, url);

      const imageFirst = primary ? responsiveImage({ url: primary.url, width: primary.width, height: primary.height, alt: title }) : '';
      const imageSecond = secondary ? responsiveImage({ url: secondary.url, width: secondary.width, height: secondary.height, alt: title }) : '';

      const imageContainerClasses = ['image', 'image--hover-second'];
      if (sameAspect) imageContainerClasses.push('image--same-aspect-ratio');

      const outerClasses = [
        'product-block',
        'cc-product-block',
        'hover-image',
        extraClass
      ];
      if (soldOut) outerClasses.push('sold-out');
      if (onSale) outerClasses.push('on-sale');

      const loopAttr = loopIndex ? ` data-loop-index="${escapeHTML(loopIndex)}"` : '';

      this.innerHTML = `
        <div class="${outerClasses.filter(Boolean).join(' ')}"${loopAttr}>
          <div class="product-block__inner">
            <div class="${imageContainerClasses.join(' ')}">
              <a class="image-inner" href="${url}" aria-label="${safeTitle}" tabindex="-1">
                <div class="image__first">
                  ${imageFirst}
                  ${labelHtml}
                </div>
                ${secondary ? `<div class="image__second">${imageSecond}</div>` : ''}
              </a>
            </div>

            <a href="${url}" class="caption upper">
              <span class="title">${safeTitle}</span>
              ${vendorHtml}
              ${priceHtml}
            </a>

            ${ratingHtml}
          </div>
        </div>
      `;
    }
  }

  customElements.define('rcc-suay-product', ProductCustomProduct);
})();
